import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { IAndITimePeriodsValue } from "@/config/date";
import { formatAdaptive } from "@/utils/number";
import { Droplets, TriangleAlert } from "lucide-react";
import { FC } from "react";
import { observer } from "mobx-react-lite";
import { ErrorContent } from "../ErrorContent/ErrorContent";
import { NotFoundContent } from "../NotFoundContent";
import { useIAndITotals } from "../useIAndISummaryData";
import { TotalVolumeHeadline } from "../TotalVolumeHeadline";

const TotalVolumesCardSkeleton: FC = () => {
	return (
		<>
			<div className="flex flex-col items-center gap-4">
				<Skeleton className="h-12 w-64 rounded-md bg-blue-50 mt-5" />

				<div className="flex flex-wrap gap-2 text-xs justify-center">
					<Skeleton className="h-6 w-28 rounded-md bg-blue-50" />
					<Skeleton className="h-6 w-28 rounded-md bg-blue-50" />
					<Skeleton className="h-6 w-28 rounded-md bg-blue-50" />
				</div>
			</div>
		</>
	);
};

export const TotalVolumesCard = observer(() => {
	const timePeriod: IAndITimePeriodsValue = "last30d";
	const {
		loading,
		loaded,
		error,
		totals,
		infiltrationPerDay,
		inflowPerDay,
		sumIIPerDay,
		percentOfTotal,
		unitLabel,
		missingConnectivityCount,
	} = useIAndITotals(timePeriod);

	const isNoData = loaded && !error && !totals;

	return (
		<Card className="shadow-sm border border-muted rounded-xl flex flex-col h-full drop-shadow-sm">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold flex items-center gap-2">
					<Droplets className="h-4 w-4 text-blue-500" />
					Total Inflow and Infiltration Volume
				</CardTitle>
			</CardHeader>

			<CardContent className="flex-1 flex flex-col justify-center">
				{loading ? (
					<TotalVolumesCardSkeleton />
				) : isNoData || !totals ? (
					<NotFoundContent message="No inflow or infiltration volume data available" />
				) : error ? (
					<ErrorContent
						error={error}
						notFoundMessage="No inflow or infiltration volume data available"
						message="Unable to load volume data"
					/>
				) : (
					<>
						<div className="mb-3">
							<TotalVolumeHeadline
								value={sumIIPerDay}
								unitLabel={unitLabel}
								percentOfTotal={percentOfTotal}
							/>
						</div>

						<div className="flex flex-wrap gap-1.5 text-[11px] justify-center">
							<div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md font-medium">
								{formatAdaptive(infiltrationPerDay)} {unitLabel} infiltration
							</div>
							<div className="bg-orange-50 text-orange-700 px-2 py-1 rounded-md font-medium">
								{formatAdaptive(inflowPerDay)} {unitLabel} inflow
							</div>
						</div>

						{missingConnectivityCount > 0 && (
							<p className="mx-auto mt-2 max-w-[260px] text-center text-[11px] text-amber-600">
								<TriangleAlert className="mr-0.5 inline h-3 w-3 align-text-bottom" />
								{missingConnectivityCount} structure{missingConnectivityCount === 1 ? " is" : "s are"}{" "}
								missing connectivity configuration, so these totals may be inaccurate
							</p>
						)}
					</>
				)}
			</CardContent>
		</Card>
	);
});
